/**
 * TemplateEditor (Phase 8i.3) — edit a document template's name, move type, and
 * content, with a field-reference panel (click to insert at the cursor) and a
 * "Preview with sample data" render through the SHARED templateEngine.
 *
 * Validation is mirrored with the backend: unique name per move_type, malformed
 * choiceboxes block save, unknown merge fields warn but allow save.
 *
 * D1 Item 7 — WHY THIS IS A SOURCE EDITOR AND NOT A WYSIWYG.
 *
 * The requirement that decides it: "must not mangle merge fields or signature
 * markers on save". Every WYSIWYG worth using (TipTap/ProseMirror, Quill, Slate)
 * parses HTML into its own document model and re-serialises on save. Anything
 * outside that model — our class names, the letterhead tables, {{signature}}
 * placeholders sitting as bare text between block elements — is normalised,
 * re-wrapped or silently dropped. On a contract, a signature block quietly
 * disappearing is not a formatting bug; it is an unsigned clause.
 *
 * So the source stays authoritative and the editor adds what a WYSIWYG was
 * wanted FOR: a formatting toolbar that inserts correct markup, a merge-field
 * picker so fields are never typed, one-click signature blocks, a live preview
 * rendered through the SHARED engine, and a section outline for navigating
 * 14,000 characters. What is saved is byte-for-byte what was authored — there is
 * no serialiser in the path that could rewrite it.
 */

import React, { useMemo, useRef, useState } from 'react';
import { CRM } from '../lib/theme';
import { useApiClient } from '../hooks/useApi';
import { Icons } from '../lib/icons';
import { useAction } from '../hooks/useAction';
import {
  renderTemplate,
  renderMergeField,
  validateTemplateContent,
  TEMPLATE_FIELD_REFERENCE,
  SAMPLE_LEAD,
  SAMPLE_CONTEXT,
} from '../lib/templateEngine';

const C = { ...CRM, mono: "'DM Mono', ui-monospace, monospace" };

// Toolbar actions insert plain markup around the selection. No parsing, no
// re-serialising — the text you author is the text that is stored.
const TOOLBAR: { label: string; title: string; before: string; after: string; block?: boolean }[] = [
  { label: 'H2', title: 'Section heading', before: '<h2>', after: '</h2>', block: true },
  { label: 'H3', title: 'Subsection heading', before: '<h3>', after: '</h3>', block: true },
  { label: 'H4', title: 'Sub-subsection heading', before: '<h4>', after: '</h4>', block: true },
  { label: 'B', title: 'Bold', before: '<strong>', after: '</strong>' },
  { label: 'I', title: 'Italic', before: '<em>', after: '</em>' },
  { label: '¶', title: 'Paragraph', before: '<p>', after: '</p>', block: true },
  { label: '1.', title: 'Numbered list', before: '<ol>\n  <li>', after: '</li>\n</ol>', block: true },
  { label: '•', title: 'Bulleted list', before: '<ul>\n  <li>', after: '</li>\n</ul>', block: true },
  { label: 'A.', title: 'Lettered list', before: '<ol type="A">\n  <li>', after: '</li>\n</ol>', block: true },
  { label: 'li', title: 'List item', before: '  <li>', after: '</li>', block: true },
];

const MOVE_TYPES = [
  { v: 'long_distance', l: 'Long Distance' },
  { v: 'local', l: 'Local' },
  { v: 'any', l: 'Both' },
];

// Compact document stylesheet for the read-only preview iframe.
const PREVIEW_CSS = `
  body { font-family: -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; color: #1a1f2b; margin: 0; padding: 24px; background: #f4f5f7; }
  .sheet { max-width: 780px; margin: 0 auto; background: #fff; padding: 36px 40px; border-radius: 8px; box-shadow: 0 2px 12px rgba(0,0,0,0.08); font-size: 13px; line-height: 1.7; }
  h2 { font-size: 20px; margin: 0 0 8px; } h3 { font-size: 14px; margin: 18px 0 6px; }
  ul { margin: 6px 0 6px 18px; padding: 0; } li { margin: 3px 0; }
  table { width: 100%; border-collapse: collapse; margin: 8px 0 4px; font-size: 13px; }
  th { text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: .5px; color: #9ca3af; border-bottom: 2px solid #e5e7eb; padding: 8px 6px; }
  td { padding: 8px 6px; border-bottom: 1px solid #eef0f3; }
  .num { text-align: right; font-family: 'DM Mono', ui-monospace, monospace; }
  .totals { margin-left: auto; width: 300px; margin-top: 12px; }
  .totals .row { display: flex; justify-content: space-between; padding: 5px 0; }
  .totals .grand { border-top: 2px solid #111827; margin-top: 6px; padding-top: 10px; font-size: 17px; font-weight: 800; }
  .muted { color: #6b7280; }
  ol { margin: 6px 0 6px 18px; padding: 0; }
  .doc-note { color: #6b7280; font-size: 12px; }
  /* Input blocks. The preview used to style none of these — the engine's
     frontend copy carried an inline margin on the choicebox to compensate,
     which is exactly the kind of second styling path lib/documentFill.ts warns
     about. The engine is now mirrored byte-for-byte from the backend, so the
     preview approximates the house stylesheet here instead. */
  .choicebox { margin: 12px 0; display: flex; flex-direction: column; gap: 6px; }
  .opt { display: flex; align-items: center; gap: 8px; }
  .opt-box { width: 14px; height: 14px; border: 1.5px solid #9ca3af; border-radius: 3px; flex: 0 0 auto; }
  .opt.chosen { font-weight: 700; }
  .sig-group { border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px 14px; margin: 14px 0; }
  .sig-label { font-size: 10px; text-transform: uppercase; letter-spacing: .5px; color: #b45309; font-weight: 700; margin: 0 0 4px; }
  .sig-intro { margin: 0 0 10px; }
  .sig-fields { display: flex; gap: 16px; align-items: flex-end; }
  .sig-line, .sig-date { flex: 1; }
  .sig-rule { border-bottom: 1px solid #9ca3af; height: 20px; }
  .sig-cap { font-size: 10px; color: #9ca3af; margin-top: 3px; }
  .init-wrap { display: inline-block; }
  .init-box { width: 56px; height: 34px; border: 1px solid #9ca3af; border-radius: 4px; }
  .init-cap { font-size: 10px; color: #9ca3af; margin-top: 3px; }
`;

interface TemplateEditorProps {
  template: any | null; // existing row, or null to create
  existing: any[]; // all templates (for uniqueness check)
  onClose: () => void;
  onSaved: () => void;
}

export function TemplateEditor({ template, existing, onClose, onSaved }: TemplateEditorProps) {
  const api = useApiClient();
  const isNew = !template;
  const [name, setName] = useState<string>(template?.name ?? '');
  const [moveType, setMoveType] = useState<string>(template?.move_type ?? 'any');
  const [content, setContent] = useState<string>(template?.content ?? '');
  const [busy, setBusy] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({ Customer: true, Job: true });
  const taRef = useRef<HTMLTextAreaElement>(null);

  // Live validation.
  const { errors, unknownFields } = useMemo(() => validateTemplateContent(content), [content]);
  const nameDup = useMemo(
    () => !!name.trim() && existing.some((t) => t.id !== template?.id && t.move_type === moveType && (t.name ?? '').trim().toLowerCase() === name.trim().toLowerCase()),
    [name, moveType, existing, template]
  );
  const nameError = !name.trim() ? 'Name is required.' : nameDup ? `A template named "${name.trim()}" already exists for this move type.` : '';
  const canSave = !busy && !nameError && errors.length === 0;

  // Insert text at the textarea cursor (not appended), preserving scroll + focus.
  const insertAtCursor = (text: string) => {
    const ta = taRef.current;
    if (!ta) { setContent((c) => c + text); return; }
    const start = ta.selectionStart ?? content.length;
    const end = ta.selectionEnd ?? content.length;
    const scroll = ta.scrollTop;
    const next = content.slice(0, start) + text + content.slice(end);
    setContent(next);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + text.length;
      ta.setSelectionRange(pos, pos);
      ta.scrollTop = scroll;
    });
  };

  /** Wrap the selection (or the cursor) in markup. Selection is preserved. */
  const wrapSelection = (before: string, after: string, block?: boolean) => {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    const scroll = ta.scrollTop;
    const sel = content.slice(start, end);
    const pad = block && start > 0 && content[start - 1] !== '\n' ? '\n' : '';
    const next = content.slice(0, start) + pad + before + sel + after + content.slice(end);
    setContent(next);
    requestAnimationFrame(() => {
      ta.focus();
      // Keep the text selected so a second action can be applied to it.
      const from = start + pad.length + before.length;
      ta.setSelectionRange(from, from + sel.length);
      ta.scrollTop = scroll;
    });
  };

  // Section outline. Scrolling a 14,000-character contract to find 3.2.1.2 is
  // the navigation problem worth solving; clicking a heading jumps to it.
  const outline = useMemo(() => {
    const out: { level: number; text: string; index: number }[] = [];
    const re = /<h([1-4])[^>]*>([\s\S]*?)<\/h\1>/gi;
    let m: RegExpExecArray | null;
    while ((m = re.exec(content))) {
      out.push({ level: Number(m[1]), text: m[2].replace(/<[^>]+>/g, '').trim(), index: m.index });
    }
    return out;
  }, [content]);

  const jumpTo = (index: number) => {
    const ta = taRef.current;
    if (!ta) return;
    ta.focus();
    ta.setSelectionRange(index, index);
    // Approximate the scroll position from how far into the text we are.
    ta.scrollTop = Math.max(0, (index / Math.max(content.length, 1)) * ta.scrollHeight - ta.clientHeight / 3);
  };

  // Version history (Item 7) — append-only; restoring writes a NEW version.
  const [history, setHistory] = useState<any[] | null>(null);
  const [historyBusy, setHistoryBusy] = useState(false);
  const loadHistory = async () => {
    if (!template?.id) return;
    setHistoryBusy(true);
    try { setHistory((await api.getTemplateVersions(template.id)).data || []); }
    catch (e: any) { window.alert(e?.message || 'Could not load history'); }
    finally { setHistoryBusy(false); }
  };
  const restore = async (version: number) => {
    if (!template?.id) return;
    if (!window.confirm(`Restore version ${version}?\n\nThis creates a NEW version rather than rewinding — nothing in the history is lost.`)) return;
    setHistoryBusy(true);
    try {
      const res: any = await api.restoreTemplateVersion(template.id, version);
      setContent(res?.data?.content ?? content);
      await loadHistory();
    } catch (e: any) { window.alert(e?.message || 'Could not restore'); }
    finally { setHistoryBusy(false); }
  };

  const saveGuard = useAction(); // one at a time: a second click is ignored
  const save = () => saveGuard.run(() => saveNow());
  const saveNow = async () => {
    if (!canSave) return;
    // Nothing changed → nothing to save. Sending an identical PATCH is not
    // harmless: server-side it used to mark the template as hand-edited, which
    // froze it on stale wording. Closing is the honest response to "save" when
    // there is nothing to write.
    const unchanged =
      !isNew &&
      name.trim() === (template?.name ?? '') &&
      moveType === (template?.move_type ?? 'any') &&
      content === (template?.content ?? '');
    if (unchanged) { onClose(); return; }

    setBusy(true);
    try {
      const payload = { name: name.trim(), move_type: moveType, content };
      if (isNew) await api.createDocumentTemplate(payload);
      else await api.updateDocumentTemplate(template.id, payload);
      onSaved();
      onClose();
    } catch (e: any) {
      window.alert(e?.message || 'Failed to save template');
    } finally {
      setBusy(false);
    }
  };

  // Field groups: merge fields by category, then dynamic blocks + inputs.
  const groups = useMemo(() => {
    const g: { label: string; rows: { label: string; insert: string; example: string }[] }[] = [];
    for (const cat of TEMPLATE_FIELD_REFERENCE.categories) {
      const rows = TEMPLATE_FIELD_REFERENCE.mergeFields
        .filter((f) => f.category === cat)
        .map((f) => ({ label: f.field, insert: `{{${f.field}}}`, example: renderMergeField(f.field, SAMPLE_LEAD, SAMPLE_CONTEXT) }));
      if (rows.length) g.push({ label: cat, rows });
    }
    g.push({
      label: 'Dynamic blocks',
      rows: TEMPLATE_FIELD_REFERENCE.dynamicBlocks.map((b) => ({ label: b.field, insert: `{{${b.field}}}`, example: b.desc })),
    });
    g.push({
      label: 'Customer inputs',
      rows: TEMPLATE_FIELD_REFERENCE.inputs.map((i: any) => ({ label: i.field, insert: i.insert as string, example: i.desc })),
    });
    return g;
  }, []);

  const previewHtml = useMemo(
    () => `<!doctype html><html><head><meta charset="utf-8"><style>${PREVIEW_CSS}</style></head><body><div class="sheet">${renderTemplate(content, SAMPLE_LEAD, SAMPLE_CONTEXT)}</div></body></html>`,
    [content]
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', zIndex: 2000, display: 'flex', justifyContent: 'center', alignItems: 'stretch' }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: C.bg, width: '100%', maxWidth: '1040px', display: 'flex', flexDirection: 'column', borderLeft: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}` }}>
        {/* Header */}
        <div style={{ background: C.panel, borderBottom: `1px solid ${C.border}`, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ flex: 1, fontSize: '15px', fontWeight: 700, color: C.text }}>{isNew ? 'New template' : 'Edit template'}{template?.is_built_in ? <span style={{ fontSize: '10px', color: C.muted, marginLeft: '8px' }}>· Built-in</span> : null}</div>
          <button onClick={onClose} style={btn(C.panel2, C.muted)} aria-label="Close"><Icons.close size={16} /></button>
        </div>

        {/* Meta */}
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', gap: '18px', flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '3px', flex: 1, minWidth: '220px' }}>
            <span style={{ fontSize: '10px', color: C.muted }}>Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} style={{ padding: '7px 9px', background: C.panel2, border: `1px solid ${nameError ? C.redLine : C.border}`, color: C.text, borderRadius: '6px', fontSize: '12px' }} />
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '10px', color: C.muted }}>Applies to</span>
            <div style={{ display: 'flex', gap: '6px' }}>
              {MOVE_TYPES.map((m) => (
                <button key={m.v} onClick={() => setMoveType(m.v)} style={{ padding: '6px 12px', borderRadius: '7px', fontSize: '12px', cursor: 'pointer', background: moveType === m.v ? C.accentFill : C.panel2, color: moveType === m.v ? 'var(--crm-on-fill)' : C.muted, border: `1px solid ${moveType === m.v ? C.accentLine : C.border}` }}>{m.l}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Body: content textarea + field panel */}
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          {/* Outline — jump to any section of a 14,000-character contract. */}
          {outline.length > 0 && (
            <div style={{ width: '200px', flexShrink: 0, padding: '12px 8px 12px 16px', overflowY: 'auto', borderRight: `1px solid ${C.border}` }}>
              <div style={{ fontSize: '10px', color: C.dim, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Outline</div>
              {outline.map((h, i) => (
                <button
                  key={i}
                  onClick={() => jumpTo(h.index)}
                  title={h.text}
                  style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer',
                           color: h.level <= 2 ? C.text : C.muted, fontSize: h.level <= 2 ? '11.5px' : '11px',
                           fontWeight: h.level <= 2 ? 600 : 400, padding: '3px 0 3px ' + ((h.level - 1) * 10) + 'px',
                           overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {h.text || '(untitled)'}
                </button>
              ))}
            </div>
          )}

          <div style={{ flex: 1, padding: '12px 0 12px 16px', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {/* Formatting toolbar — inserts markup around the selection. There is
                no parse/re-serialise step, so merge fields and signature markers
                cannot be rewritten by it. */}
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '8px' }}>
              {TOOLBAR.map((t) => (
                <button
                  key={t.label} title={t.title}
                  onClick={() => wrapSelection(t.before, t.after, t.block)}
                  style={{ background: C.panel2, border: `1px solid ${C.border}`, color: C.text, borderRadius: '5px',
                           padding: '3px 9px', fontSize: '11px', cursor: 'pointer', fontWeight: t.label === 'B' ? 800 : 500,
                           fontStyle: t.label === 'I' ? 'italic' : 'normal', minWidth: '28px' }}
                >
                  {t.label}
                </button>
              ))}
              <span style={{ width: '1px', background: C.border, margin: '0 4px' }} />
              {/* Signature blocks get their own prominent control — they are the
                  most important element on a contract page. */}
              <button
                title="Insert a signature block"
                onClick={() => insertAtCursor('\n{{signature}}\n')}
                style={{ background: `${C.amberTint}`, border: `1px solid ${C.amberLine}`, color: C.amber, borderRadius: '5px',
                         padding: '3px 10px', fontSize: '11px', cursor: 'pointer', fontWeight: 700 }}
              >
                + Signature block
              </button>
              {template?.id && (
                <button
                  onClick={() => (history ? setHistory(null) : loadHistory())}
                  style={{ marginLeft: 'auto', background: 'none', border: `1px solid ${C.border}`, color: C.muted,
                           borderRadius: '5px', padding: '3px 10px', fontSize: '11px', cursor: 'pointer' }}
                >
                  {history ? 'Hide history' : 'Version history'}
                </button>
              )}
            </div>

            {history && (
              <div style={{ maxHeight: '160px', overflowY: 'auto', border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px', marginBottom: '8px', background: C.panel2 }}>
                <div style={{ fontSize: '10px', color: C.dim, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                  Version history {historyBusy ? '· loading…' : ''}
                </div>
                {history.length === 0 && <div style={{ fontSize: '11px', color: C.dim }}>No saved versions yet. The next save creates version 1.</div>}
                {history.map((v) => (
                  <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '3px 0', fontSize: '11px', color: C.muted }}>
                    <span style={{ fontFamily: C.mono, color: C.text, minWidth: '28px' }}>v{v.version}</span>
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {new Date(v.created_at).toLocaleString()}{v.note ? ` · ${v.note}` : ''}
                    </span>
                    <button onClick={() => setContent(v.content)} disabled={historyBusy}
                      style={{ background: 'none', border: `1px solid ${C.border}`, color: C.muted, borderRadius: '4px', padding: '1px 7px', fontSize: '10px', cursor: 'pointer' }}>
                      View
                    </button>
                    <button onClick={() => restore(v.version)} disabled={historyBusy}
                      style={{ background: 'none', border: `1px solid ${C.accentSoftLine}`, color: C.accent, borderRadius: '4px', padding: '1px 7px', fontSize: '10px', cursor: 'pointer' }}>
                      Restore
                    </button>
                  </div>
                ))}
              </div>
            )}

            <textarea
              ref={taRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              spellCheck={false}
              placeholder="Write the template. Click a field on the right to insert it at the cursor."
              style={{ flex: 1, resize: 'none', width: '100%', background: C.panel2, border: `1px solid ${C.border}`, color: C.text, borderRadius: '8px', padding: '12px', fontSize: '12.5px', fontFamily: C.mono, lineHeight: 1.6, boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ width: '270px', flexShrink: 0, padding: '12px 16px', overflowY: 'auto', borderLeft: `1px solid ${C.border}` }}>
            <div style={{ fontSize: '10px', color: C.dim, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Available fields</div>
            {groups.map((grp) => {
              const open = openGroups[grp.label] ?? false;
              return (
                <div key={grp.label} style={{ marginBottom: '4px' }}>
                  <button onClick={() => setOpenGroups((p) => ({ ...p, [grp.label]: !open }))} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: C.text, cursor: 'pointer', fontSize: '12px', fontWeight: 600, padding: '5px 0', textAlign: 'left' }}>
                    <span style={{ color: C.muted }}>{open ? '▾' : '▸'}</span>{grp.label}
                  </button>
                  {open && grp.rows.map((r) => (
                    <button key={r.label} onClick={() => insertAtCursor(r.insert)} title={r.example} style={{ display: 'block', width: '100%', textAlign: 'left', background: C.panel2, border: `1px solid ${C.border}`, color: C.muted, borderRadius: '6px', padding: '5px 9px', fontSize: '11px', fontFamily: C.mono, cursor: 'pointer', marginBottom: '4px' }}>
                      {r.label}
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        {/* Validation notices */}
        {(errors.length > 0 || unknownFields.length > 0) && (
          <div style={{ padding: '8px 16px', borderTop: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {errors.map((er, i) => <div key={i} style={{ fontSize: '11px', color: C.red, display: 'flex', alignItems: 'center', gap: '5px' }}><Icons.ban size={12} /> {er}</div>)}
            {unknownFields.length > 0 && (
              <div style={{ fontSize: '11px', color: C.amber }}>
                <Icons.warning size={12} style={{ verticalAlign: 'text-bottom' }} /> Note: {unknownFields.length} unrecognized field{unknownFields.length === 1 ? '' : 's'} ({unknownFields.map((f) => `{{${f}}}`).join(', ')}) will render literally.
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div style={{ background: C.panel, borderTop: `1px solid ${C.border}`, padding: '12px 16px', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button onClick={() => setShowPreview(true)} style={btn(C.panel2, C.text)}>Preview with sample data</button>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={btn(C.panel2, C.muted)}>Cancel</button>
          <button onClick={save} disabled={!canSave} style={{ ...btn(C.accentFill, 'var(--crm-on-fill)'), opacity: canSave ? 1 : 0.5 }}>Save</button>
        </div>
      </div>

      {/* Preview overlay */}
      {showPreview && (
        <div onClick={(e) => { e.stopPropagation(); }} style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', zIndex: 2100, display: 'flex', flexDirection: 'column' }}>
          <div style={{ background: C.panel, borderBottom: `1px solid ${C.border}`, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ flex: 1, fontSize: '13px', color: C.text }}>
              Preview <span style={{ color: C.amber, fontSize: '11px', marginLeft: '8px' }}>Sample data — not a real job</span>
            </div>
            <button onClick={() => setShowPreview(false)} style={btn(C.accentFill, 'var(--crm-on-fill)')}>Back to editing</button>
          </div>
          <iframe title="template-preview" srcDoc={previewHtml} style={{ flex: 1, border: 'none', width: '100%', background: '#f4f5f7' }} />
        </div>
      )}
    </div>
  );
}

function btn(bg: string, color: string): React.CSSProperties {
  return { padding: '7px 13px', background: bg, color, border: `1px solid ${bg === C.panel2 ? C.border : bg}`, borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 };
}
